import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { CopyableReadonlyField } from '@/components/forms/copyable-readonly-field'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'

interface TemporaryPasswordModalProps {
  email: string
  onClose: () => void
  open: boolean
  password?: string
  title?: string
  titleHelp?: string
}

export function TemporaryPasswordModal({
  email,
  onClose,
  open,
  password,
  title,
  titleHelp,
}: TemporaryPasswordModalProps) {
  const { t } = useTranslation('adminOrganization')
  const { showFeedback } = useFeedback()
  const passwordValue = password ?? '-'

  const copyValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      showFeedback({
        message: t('temporaryPassword.copied'),
        tone: 'success',
      })
    } catch {
      showFeedback({
        message: t('temporaryPassword.copyFailed'),
        tone: 'error',
      })
    }
  }

  return (
    <Modal
      closeLabel={t('temporaryPassword.close')}
      footer={
        <Button onClick={onClose} size="sm">
          {t('actions.confirm')}
        </Button>
      }
      onClose={onClose}
      open={open}
      title={title ?? t('temporaryPassword.title')}
      titleHelp={titleHelp}
    >
      <div className="grid gap-4">
        <CopyableReadonlyField
          copyLabel={t('temporaryPassword.copyAccount')}
          label={t('temporaryPassword.account')}
          onCopy={() => void copyValue(email)}
          showLabel
          value={email}
        />
        <CopyableReadonlyField
          copyLabel={t('temporaryPassword.copyPassword')}
          label={t('temporaryPassword.password')}
          onCopy={() => void copyValue(passwordValue)}
          showLabel
          value={passwordValue}
        />
      </div>
    </Modal>
  )
}
